import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SidebarProvider } from './context/SidebarContext';
import ThemeAwareToaster from './components/ThemeAwareToaster';
import { useNotifications } from './hooks/useNotifications';
import NotificationPermissionBanner from './components/NotificationPermissionBanner';
import NotificationManager from './components/NotificationManager';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MyDashboard from './pages/MyDashboard';
import TicketsList from './pages/TicketsList';
import TicketCreate from './pages/TicketCreate';
import TicketDetail from './pages/TicketDetail';
import MasterSetup from './pages/MasterSetup';
import Users from './pages/Users';
import ClientTickets from './pages/ClientTickets';
import ClientTicketCreate from './pages/ClientTicketCreate';
import ClientTicketDetail from './pages/ClientTicketDetail';
import Notifications from './pages/Notifications';
import AdminSettings from './pages/AdminSettings';
import './App.css';

// Notification wrapper component
const NotificationWrapper = ({ children }) => {
  const { user } = useAuth();
  
  // Initialize WebSocket connection for notifications
  useNotifications(user);
  
  return children;
};

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if user has allowed role
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect client users to their tickets page
    if (user.role === 'Client') {
      return <Navigate to="/tickets" />;
    }
    // Redirect other users to dashboard
    return <Navigate to="/dashboard" />;
  }
  
  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SidebarProvider>
          <BrowserRouter>
            <NotificationWrapper>
          <NotificationPermissionBanner />
          <NotificationManager />
          <Routes>
            <Route path="/login" element={<Login />} />
            
            {/* Root - redirect based on role */}
            <Route path="/" element={<PrivateRoute><RoleBasedHome /></PrivateRoute>} />
            
            {/* Shared ticket detail route - shows different component based on role */}
            <Route path="/tickets/:id" element={<PrivateRoute><RoleBasedTicketDetail /></PrivateRoute>} />
            
            {/* Shared Notifications Route - All users */}
            <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
            
            {/* Client Routes */}
            <Route path="/tickets" element={<PrivateRoute allowedRoles={['Client']}><ClientTickets /></PrivateRoute>} />
            <Route path="/tickets/new" element={<PrivateRoute allowedRoles={['Client']}><ClientTicketCreate /></PrivateRoute>} />
            
            {/* Admin/Manager/Engineer Routes */}
            <Route path="/my-dashboard" element={<PrivateRoute allowedRoles={['Admin', 'Manager', 'Engineer']}><MyDashboard /></PrivateRoute>} />
            <Route path="/dashboard" element={<PrivateRoute allowedRoles={['Admin', 'Manager', 'Engineer']}><Dashboard /></PrivateRoute>} />
            <Route path="/all-tickets" element={<PrivateRoute allowedRoles={['Admin', 'Manager', 'Engineer']}><TicketsList /></PrivateRoute>} />
            <Route path="/my-tickets" element={<PrivateRoute allowedRoles={['Admin', 'Manager', 'Engineer']}><TicketsList myTickets /></PrivateRoute>} />
            <Route path="/create-ticket" element={<PrivateRoute allowedRoles={['Admin', 'Manager', 'Engineer']}><TicketCreate /></PrivateRoute>} />
            <Route path="/master-setup" element={<PrivateRoute allowedRoles={['Admin', 'Manager']}><MasterSetup /></PrivateRoute>} />
            <Route path="/users" element={<PrivateRoute allowedRoles={['Admin', 'Manager']}><Users /></PrivateRoute>} />
            <Route path="/admin/settings" element={<PrivateRoute allowedRoles={['Admin']}><AdminSettings /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ThemeAwareToaster />
        </NotificationWrapper>
          </BrowserRouter>
        </SidebarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Component to redirect to appropriate home based on role
const RoleBasedHome = () => {
  const { user } = useAuth();
  
  if (user?.role === 'Client') {
    return <Navigate to="/tickets" />;
  }
  
  return <Navigate to="/my-dashboard" />;
};

// Component to show appropriate ticket detail based on role
const RoleBasedTicketDetail = () => {
  const { user } = useAuth();
  
  if (user?.role === 'Client') {
    return <ClientTicketDetail />;
  }
  
  return <TicketDetail />;
};

export default App;